import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import api from '../../services/api';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PageContainer from '../../common/components/PageContainer';
import useAlerta from '../../common/components/useAlerta';
import AppSnackbar from '../../common/components/AppSnackbar';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const formatarMoeda = (valor) =>
    Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarDataBR = (dataISO) => {
    if (!dataISO) return '';
    const [ano, mes, dia] = dataISO.split('-');
    return `${dia}/${mes}/${ano}`;
};

function FluxoCaixa() {

    const { register, handleSubmit, setValue, watch, reset } = useForm();
    const { alerta, mostrarAlerta, fecharAlerta } = useAlerta();
    const [registros, setRegistros] = useState([]);

    const hoje = new Date();
    const [mesSelecionado, setMesSelecionado] = useState(hoje.getMonth() + 1);
    const [anoSelecionado, setAnoSelecionado] = useState(hoje.getFullYear());

    const buscarRegistros = async () => {
        try {
            const response = await api.get('/fluxocaixa');
            setRegistros(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Erro ao buscar fluxo de caixa:", error);
            mostrarAlerta('Erro ao carregar o fluxo de caixa!', 'error');
        }
    };

    useEffect(() => {
        const hojeISO = new Date().toISOString().split('T')[0];
        setValue("data", hojeISO);
        buscarRegistros();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setValue]);

    const registrosFiltrados = registros
        .filter((item) => {
            const [ano, mes] = item.data.split('-').map(Number);
            return mes === mesSelecionado && ano === anoSelecionado;
        })
        .sort((a, b) => (a.data < b.data ? 1 : -1));

    const totalEntradas = registrosFiltrados
        .filter((item) => item.tipo === 'Entrada')
        .reduce((acc, item) => acc + Number(item.valor), 0);

    const totalSaidas = registrosFiltrados
        .filter((item) => item.tipo !== 'Entrada')
        .reduce((acc, item) => acc + Number(item.valor), 0);

    const saldo = totalEntradas - totalSaidas;

    const anosDisponiveis = Array.from(
        new Set([hoje.getFullYear(), ...registros.map((item) => Number(item.data.split('-')[0]))])
    ).sort((a, b) => b - a);

    const agrupadoPorData = {};
    registrosFiltrados.forEach((item) => {
        if (!agrupadoPorData[item.data]) {
            agrupadoPorData[item.data] = { entrada: 0, saida: 0 };
        }
        if (item.tipo === 'Entrada') {
            agrupadoPorData[item.data].entrada += Number(item.valor);
        } else {
            agrupadoPorData[item.data].saida += Number(item.valor);
        }
    });
    const labelsGrafico = Object.keys(agrupadoPorData).sort();
    const chartData = {
        labels: labelsGrafico.map(formatarDataBR),
        datasets: [
            {
                label: 'Entradas',
                data: labelsGrafico.map((data) => agrupadoPorData[data].entrada),
                backgroundColor: '#4caf50',
            },
            {
                label: 'Saídas',
                data: labelsGrafico.map((data) => agrupadoPorData[data].saida),
                backgroundColor: '#d32f2f',
            },
        ],
    };

    const onSubmit = async (data) => {
        const valorNumerico = parseFloat(
            data.valor.replace(/[^\d,]/g, '').replace(',', '.')
        ).toFixed(2);

        const formData = {
            data: data.data,
            descricao: data.descricao,
            tipo: data.tipo,
            formaPag: data.formaPag,
            valor: valorNumerico,
        };

        try {
            await api.post('/fluxocaixa', formData);
            mostrarAlerta('Registro salvo com sucesso!', 'success');

            const hojeISO = new Date().toISOString().split('T')[0];
            reset({ data: hojeISO });

            buscarRegistros();
        } catch (error) {
            mostrarAlerta('Erro ao salvar o registro!', 'error');
        }

        setTimeout(fecharAlerta, 3000);
    };

    const valor = watch("valor", "");

    const formatarValor = (e) => {
        let valorDigitado = e.target.value.replace(/\D/g, "");
        valorDigitado = (parseInt(valorDigitado || 0, 10) / 100).toFixed(2);

        if (!isNaN(valorDigitado)) {
            setValue("valor", `R$ ${valorDigitado.replace('.', ',')}`);
        }
    };

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Fluxo de Caixa
                </Typography>
                <Stack direction="row" spacing={1}>
                    <TextField
                        select
                        size="small"
                        label="Mês"
                        value={mesSelecionado}
                        onChange={(e) => setMesSelecionado(Number(e.target.value))}
                        sx={{ minWidth: 140 }}
                    >
                        {MESES.map((mes, index) => (
                            <MenuItem key={index + 1} value={index + 1}>{mes}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        size="small"
                        label="Ano"
                        value={anoSelecionado}
                        onChange={(e) => setAnoSelecionado(Number(e.target.value))}
                        sx={{ minWidth: 100 }}
                    >
                        {anosDisponiveis.map((ano) => (
                            <MenuItem key={ano} value={ano}>{ano}</MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar sx={{ bgcolor: 'success.light', color: 'success.dark', width: 44, height: 44 }}>
                                    <TrendingUpIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Entradas no período</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                                        {formatarMoeda(totalEntradas)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar sx={{ bgcolor: 'error.light', color: 'error.dark', width: 44, height: 44 }}>
                                    <TrendingDownIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Saídas no período</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                                        {formatarMoeda(totalSaidas)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Avatar sx={{ bgcolor: 'primary.light', color: '#fff', width: 44, height: 44 }}>
                                    <AccountBalanceWalletOutlinedIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Saldo do período</Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700, color: saldo >= 0 ? 'success.main' : 'error.main' }}
                                    >
                                        {formatarMoeda(saldo)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            {labelsGrafico.length > 0 ? (
                                <Bar
                                    data={chartData}
                                    options={{
                                        responsive: true,
                                        plugins: {
                                            legend: { position: 'top' },
                                            title: { display: false },
                                        },
                                    }}
                                />
                            ) : (
                                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                                    Nenhum lançamento no período selecionado.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                Lançamentos do período
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Data</TableCell>
                                            <TableCell>Descrição</TableCell>
                                            <TableCell>Tipo</TableCell>
                                            <TableCell align="right">Valor</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {registrosFiltrados.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>{formatarDataBR(item.data)}</TableCell>
                                                <TableCell>{item.descricao}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        color={item.tipo === 'Entrada' ? 'success' : 'error'}
                                                        label={item.tipo === 'Entrada' ? 'Entrada' : 'Saída'}
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{ fontWeight: 600, color: item.tipo === 'Entrada' ? 'success.main' : 'error.main' }}
                                                >
                                                    {item.tipo === 'Entrada' ? '+ ' : '- '}
                                                    {formatarMoeda(item.valor)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {registrosFiltrados.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                                    Nenhum lançamento neste período.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 3, position: { md: 'sticky' }, top: { md: 16 } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Novo lançamento
                        </Typography>
                        <Stack spacing={2}>
                            <TextField {...register("data")} label="Data" type="date" InputLabelProps={{ shrink: true }} required fullWidth />
                            <TextField {...register("descricao")} label="Descrição" required fullWidth InputLabelProps={{ shrink: true }} />
                            <TextField {...register("tipo")} select label="Tipo" defaultValue="" required fullWidth>
                                <MenuItem value="">Selecione</MenuItem>
                                <MenuItem value="Entrada">Entrada</MenuItem>
                                <MenuItem value="Saida">Saída</MenuItem>
                            </TextField>
                            <TextField {...register("formaPag")} select label="Forma de pagamento" defaultValue="" required fullWidth>
                                <MenuItem value="">Selecione</MenuItem>
                                <MenuItem value="Dinheiro">Espécie</MenuItem>
                                <MenuItem value="Pix">Pix</MenuItem>
                                <MenuItem value="Cartão crédito">Cartão crédito</MenuItem>
                                <MenuItem value="Cartão débito">Cartão débito</MenuItem>
                            </TextField>
                            <TextField
                                {...register("valor")}
                                label="Valor"
                                value={valor}
                                onChange={formatarValor}
                                required
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                            <Button type="submit" variant="contained" color="secondary" size="large" fullWidth>
                                Registrar
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            <AppSnackbar alerta={alerta} onClose={fecharAlerta} />
        </PageContainer>
    );
}

export default FluxoCaixa;
